import type { Agent, AgentVoice } from '@tunasong/schemas'
import { agentInstructionsMaxLength, agentVoices, defaultAgentName, isDefaultAgentName } from '@tunasong/schemas'
import { usualVoice, type VoiceChoice } from './personalize-copy.js'

/** What the Personalize dialog edits (Joni §2). `name` is '' when the agent still has its stock name. */
export interface PersonaForm {
  name: string
  voice: VoiceChoice
  instructions: string
}

/** The persona fields as the server stores them: `null` clears voice and instructions. */
export interface PersonaWrite {
  name: string
  voice: AgentVoice | null
  instructions: string | null
}

type PersonaSource = Pick<Agent, 'name' | 'voice' | 'instructions' | 'definitionId'>

function isAgentVoice(value: string): value is AgentVoice {
  return (agentVoices as readonly string[]).includes(value)
}

/** The name the agent falls back to: its definition's stock name, the "Leave blank to use …" value. */
export const defaultNameOf = (agent: PersonaSource) => defaultAgentName(agent.definitionId ?? '')

/** The form as the agent stands. A stock name opens blank, so the placeholder shows what blank means. */
export const formOf = (agent: PersonaSource): PersonaForm => ({
  name: isDefaultAgentName(agent) ? '' : (agent.name?.trim() ?? ''),
  voice: agent.voice ?? usualVoice,
  instructions: agent.instructions ?? '',
})

/** The form with every field at its default: what Reset puts back. */
export const defaultForm: PersonaForm = { name: '', voice: usualVoice, instructions: '' }

const sameForm = (a: PersonaForm, b: PersonaForm) =>
  a.name.trim() === b.name.trim() && a.voice === b.voice && a.instructions.trim() === b.instructions.trim()

export const isDirty = (opened: PersonaForm, current: PersonaForm) => !sameForm(opened, current)

/** Reset shows only while something differs from the defaults (Joni §2 Footer). */
export const isAtDefault = (form: PersonaForm) => sameForm(form, defaultForm)

/** Characters past the cap, counted on the trimmed text the server will store; 0 when within it. */
export const overBy = (form: PersonaForm) => Math.max(0, form.instructions.trim().length - agentInstructionsMaxLength)

export const canSave = (opened: PersonaForm, current: PersonaForm) => isDirty(opened, current) && overBy(current) === 0

/** What Save sends. A blank name writes the stock name back, so the agent keeps a name everywhere. */
export const writeOf = (form: PersonaForm, agent: PersonaSource): PersonaWrite => {
  const instructions = form.instructions.trim()
  return {
    name: form.name.trim() || defaultNameOf(agent),
    voice: isAgentVoice(form.voice) ? form.voice : null,
    instructions: instructions.length > 0 ? instructions : null,
  }
}

/** The agent's current persona as a write, for Undo: putting back exactly what was there. */
export const writeFromAgent = (agent: PersonaSource): PersonaWrite => ({
  name: agent.name?.trim() || defaultNameOf(agent),
  voice: agent.voice ?? null,
  instructions: agent.instructions?.trim() || null,
})

/**
 * The polite live-region message for a change in the instructions' length (Joni §5): only at 100 and 20
 * characters left and on crossing the limit, never on every keystroke. Undefined when nothing is worth saying.
 */
export const lengthAnnouncement = (previous: number, current: number): string | undefined => {
  const max = agentInstructionsMaxLength
  const crossed = (threshold: number) => previous < threshold && current >= threshold
  if (crossed(max + 1)) {
    return `Over the limit by ${current - max}`
  }
  if (crossed(max - 20)) {
    return '20 characters left'
  }
  if (crossed(max - 100)) {
    return '100 characters left'
  }
  return undefined
}
